import React from 'react';
import { useTheme } from '@mui/material/styles';

type HomeSkeletonProps = {
  fullHeight?: boolean;
  className?: string;
};

const HomeSkeleton: React.FC<HomeSkeletonProps> = ({ fullHeight = true, className = '' }) => {
  const theme = useTheme();
  
  const skeletonColors = {
    page: theme.palette.mode === 'light' ? '#ffffff' : '#10141C',
    block: theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e',
    shine: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)'
  };

  const skeletonStyles = `
    .home-skeleton-page { 
      padding: 24px; 
      background: ${skeletonColors.page}; 
      display: flex; 
      flex-direction: column; 
      gap: 32px; 
    }
    .home-skeleton-page.full { min-height: 100vh; }
    .home-skeleton-header { 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      height: 50px; 
      width: 100%; 
      max-width: 1152px; 
      margin: 0 auto; 
    }
    .home-skel-logo { 
      width: 130px; 
      height: 32px; 
      background: ${skeletonColors.block}; 
      border-radius: 8px; 
    }
    .home-skel-avatar { 
      width: 32px; 
      height: 32px; 
      background: ${skeletonColors.block}; 
      border-radius: 50%; 
    }

    .home-skeleton-content { 
      display: flex; 
      flex-direction: column; 
      gap: 20px; 
      height: 100%; 
      max-width: 1152px; 
      margin: 0 auto; 
      width: 100%; 
    }
    .home-skeleton-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .home-skel-card { 
      background: ${skeletonColors.block}; 
      border-radius: 12px; 
      height: 184px; 
      position: relative; 
      overflow: hidden;
      box-shadow: none;
    }
    .home-skel-right { 
      display: flex; 
      flex-direction: column; 
      align-items: flex-end; 
      gap: 10px; 
      height: 100%; 
    }

    .home-skel-table { display: flex; flex-direction: column; gap: 10px; }
    .home-skel-thead { 
      background: ${skeletonColors.block}; 
      height: 56px; 
      border-radius: 12px; 
      position: relative; 
      overflow: hidden;
      box-shadow: none;
    }
    .home-skel-row { 
      background: ${skeletonColors.block}; 
      height: 84px; 
      border-radius: 12px; 
      position: relative; 
      overflow: hidden;
      box-shadow: none;
    }
    .home-skel-row + .home-skel-row { margin-top: 8px; }
    .home-td { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
      padding: 0 12px; 
      height: 100%; 
    }
    .home-td.right { justify-content: flex-end; gap: 10px; }
    .home-skel-checkbox { 
      width: 16px; 
      height: 16px; 
      border-radius: 4px; 
      background: ${skeletonColors.block}; 
    }
    .home-skel-cell { 
      height: 12px; 
      border-radius: 6px; 
      background: ${skeletonColors.block}; 
    }
    .home-w-40 { width: 40%; } 
    .home-w-50 { width: 50%; } 
    .home-w-60 { width: 60%; } 
    .home-w-35 { width: 35%; }
    .home-w-55 { width: 55%; }
    .home-skel-pill { 
      width: 62px; 
      height: 18px; 
      border-radius: 999px; 
      background: ${skeletonColors.block}; 
    }
    .home-skel-menu { 
      width: 18px; 
      height: 18px; 
      border-radius: 6px; 
      background: ${skeletonColors.block}; 
    }

    .home-skel-footer { 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      margin-top: 50px; 
      gap: 16px; 
    }
    .home-skel-notes { 
      display: grid; 
      grid-template-rows: repeat(3, 12px); 
      gap: 10px; 
      width: 60%; 
    }
    .home-skel-note { 
      background: ${skeletonColors.block}; 
      border-radius: 6px; 
    }
    .home-skel-cta { 
      background: ${skeletonColors.block}; 
      width: 280px; 
      height: 48px; 
      border-radius: 8px; 
      position: relative; 
      overflow: hidden; 
    }

    .home-shimmer { position: relative; overflow: hidden; }
    .home-shimmer::after { 
      content: ""; 
      position: absolute; 
      inset: 0; 
      transform: translateX(-150%);
      background: linear-gradient(90deg, transparent 0%, ${skeletonColors.shine} 50%, transparent 100%);
      animation: homeShimmerAnim 1.6s ease-in-out infinite; 
    }
    @keyframes homeShimmerAnim { 
      0% { transform: translateX(-150%); } 
      100% { transform: translateX(150%); } 
    }

    @media (max-width: 960px) {
      .home-skeleton-cards { grid-template-columns: 1fr; }
      .home-skel-notes { width: 100%; }
      .home-skel-cta { width: 100%; }
    }
  `;

  return (
    <div className={`home-skeleton-page${fullHeight ? ' full' : ''} ${className}`.trim()}>
      <style>{skeletonStyles}</style>
      <div className="home-skeleton-header">
        <div className="home-skel-logo home-shimmer" />
        <div className="home-skel-avatar home-shimmer" />
      </div>

      <div className="home-skeleton-content">
        <div className="home-skeleton-cards">
          <div className="home-skel-card home-shimmer" />
          <div className="home-skel-card home-shimmer">
            <div className="home-skel-right" />
          </div>
        </div>

        <div className="home-skel-table">
          <div className="home-skel-thead home-shimmer" />

          <div className="home-skel-row home-shimmer">
            <div className="home-td">
              <div className="home-skel-checkbox" />
              <div className="home-skel-cell home-w-40" />
            </div>
            <div className="home-td"><div className="home-skel-cell home-w-60" /></div>
            <div className="home-td"><div className="home-skel-cell home-w-40" /></div>
            <div className="home-td"><div className="home-skel-cell home-w-50" /></div>
            <div className="home-td right">
              <div className="home-skel-pill" />
              <div className="home-skel-menu" />
            </div>
          </div>

          <div className="home-skel-row home-shimmer">
            <div className="home-td">
              <div className="home-skel-checkbox" />
              <div className="home-skel-cell home-w-35" />
            </div>
            <div className="home-td"><div className="home-skel-cell home-w-55" /></div>
            <div className="home-td"><div className="home-skel-cell home-w-40" /></div>
            <div className="home-td"><div className="home-skel-cell home-w-40" /></div>
            <div className="home-td right">
              <div className="home-skel-pill" />
              <div className="home-skel-menu" />
            </div>
          </div>
        </div>

        <div className="home-skel-footer">
          <div className="home-skel-notes">
            <div className="home-skel-note home-shimmer" />
            <div className="home-skel-note home-shimmer" />
            <div className="home-skel-note home-shimmer" />
          </div>
          <div className="home-skel-cta home-shimmer" />
        </div>
      </div>
    </div>
  );
};

export default HomeSkeleton;
